import { useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useSidebarCollapsed, useTheme, useSetTheme, useAppStore } from '@/store'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { NotificationPermissionPrompt } from '@/components/NotificationPermissionPrompt'
import { TrialCountdownBanner } from '@/components/TrialCountdownBanner'
import { DowngradeNotice } from '@/components/DowngradeNotice'
import { LayoutDashboard, Clock, Star, Calendar, MoreHorizontal } from 'lucide-react'

// ── Mobile bottom tab bar items ─────────────────────────────────────────────
const BOTTOM_TABS = [
  { to: '/dashboard',     icon: LayoutDashboard, label: 'Home' },
  { to: '/daily-timings', icon: Clock,            label: 'Daily' },
  { to: '/birth-chart',   icon: Star,             label: 'Chart' },
  { to: '/muhurta',       icon: Calendar,         label: 'Muhurta' },
] as const

export function AppLayout() {
  const collapsed = useSidebarCollapsed()
  const theme = useTheme()
  const setTheme = useSetTheme()
  const location = useLocation()
  const { openMobileSidebar } = useAppStore()

  // On first visit with no stored preference, honour prefers-color-scheme
  useEffect(() => {
    try {
      const stored = localStorage.getItem('shubh-time-store')
      const hasStoredTheme = stored && JSON.parse(stored)?.state?.theme
      if (!hasStoredTheme && window.matchMedia('(prefers-color-scheme: light)').matches) {
        setTheme('light')
      }
    } catch {
      // ignore parse errors
    }
  }, []) // intentionally run once on mount

  // Sync theme class on <html>
  useEffect(() => {
    const html = document.documentElement
    if (theme === 'light') {
      html.classList.add('light')
    } else {
      html.classList.remove('light')
    }
  }, [theme])

  return (
    <div className="min-h-screen bg-cosmic-void">
      {/* Star field overlay — hidden in light mode via .star-field-overlay CSS class */}
      <div
        className="star-field-overlay fixed inset-0 pointer-events-none z-0"
        aria-hidden="true"
      />

      <Sidebar />
      <Header />

      {/* Main content */}
      <main
        className={cn(
          'relative z-10',
          'pt-[var(--header-height)]',
          'min-h-screen',
          'transition-[padding-left] duration-300 ease-cosmic',
          // Mobile: no left padding (sidebar overlays); Desktop: offset by sidebar
          'pl-0',
          collapsed ? 'md:pl-[60px]' : 'md:pl-[var(--sidebar-width)]',
          // Extra bottom padding on mobile for the tab bar
          'pb-16 md:pb-0',
        )}
      >
        {/* Conversion banners — mutually exclusive; below fixed header */}
        <TrialCountdownBanner />
        <DowngradeNotice />
        <NotificationPermissionPrompt />

        {/* Page content with transition animation */}
        <div
          key={location.key}
          className="p-4 sm:p-6 lg:p-8 animate-page-enter"
        >
          <Outlet />
        </div>
      </main>

      {/* ── Mobile bottom tab bar (hidden md+) ──────────────────────────── */}
      <nav
        className={cn(
          'fixed bottom-0 left-0 right-0 z-30 md:hidden',
          'h-16 flex items-stretch',
          'bg-cosmic-nebula/95 backdrop-blur-xl border-t border-white/[0.08]',
          'safe-area-inset-bottom',
        )}
        aria-label="Primary navigation"
      >
        {BOTTOM_TABS.map(tab => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                'flex-1 flex flex-col items-center justify-center gap-1 transition-colors duration-150',
                isActive
                  ? 'text-gold'
                  : 'text-star-muted active:text-star-soft',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    'w-10 h-7 rounded-xl flex items-center justify-center transition-all duration-150',
                    isActive ? 'bg-gold/15' : 'bg-transparent',
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                </span>
                <span className="text-2xs font-medium leading-none">{tab.label}</span>
              </>
            )}
          </NavLink>
        ))}

        {/* More — opens sidebar */}
        <button
          onClick={openMobileSidebar}
          className="flex-1 flex flex-col items-center justify-center gap-1 text-star-muted active:text-star-soft transition-colors duration-150"
          aria-label="More navigation"
        >
          <span className="w-10 h-7 rounded-xl flex items-center justify-center">
            <MoreHorizontal className="w-5 h-5" />
          </span>
          <span className="text-2xs font-medium leading-none">More</span>
        </button>
      </nav>
    </div>
  )
}
